import { bunchBounds, seriesActiveAt, type ScheduleSeriesView } from "@edu/shared";

/**
 * Свёртка списка серий: пары отдельно, пучки групп отдельно.
 *
 * ⚠️ **Своим файлом, потому что это чистый счёт, а не разметка** (и потому что
 * экран с ним перерастал четыреста строк). Свёртку пучка делает КЛИЕНТ, новой
 * ручки под неё нет: раздел 3 `docs/group-lessons-design.md`, пункт 3. Ручка
 * звала бы названия заготовок на каждую группу, то есть двадцать групп это
 * двадцать запросов, а весь расчёт здесь это десяток строк поверх правил,
 * которые уже написаны и покрыты тестами в `@edu/shared`.
 *
 * ⚠️ **Ни одной своей арифметики по датам.** Границы пучка считает `bunchBounds`,
 * а какие серии в него входят, решает `seriesActiveAt`, обе из `@edu/shared`, и
 * обе зовёт сервер на том же входном наборе (`groupScheduleView` в
 * `apps/api/src/repo/schedule-groups.ts`). Вторая копия правила разошлась бы с
 * первой молча: расхождение в датах не падает и не логируется, его видно только
 * глазами и только тому, у кого в пучке есть ушедший участник.
 */

/**
 * Пучок группы, свёрнутый в строку.
 *
 * Полей ровно столько, сколько показывает строка: считается пучок один раз, при
 * разборе ответа, а не по разу на каждый слот при отрисовке.
 */
export interface BunchView {
  groupId: string;
  /** Название группы. `null` не бывает, разбор у самой строки. */
  title: string | null;
  /**
   * Сколько участников у действующего расписания. `null` это «расписание
   * кончилось»: действующих серий не осталось ни одной.
   *
   * ⚠️ **Ноля здесь не бывает, и это правка по итогам ревью 30.08.2026.**
   * Сначала у распущенного пучка стоял ноль, и строка получалась «Группа, 0
   * учеников» у группы, в которой люди есть: число отвечало не на тот вопрос.
   * Вопрос строки это «сколько человек ходит», а у кончившегося расписания
   * верного ответа на него нет вовсе, поэтому число не показывается, а сама
   * строка говорит словами, что расписание кончилось.
   *
   * ⚠️ **Это НЕ то же число, что `group.size` в строке сетки**, и совпадать они
   * не обязаны. Там считаются те, кому занятие действительно производится, и
   * отвязанная пара занятий не даёт; здесь считаются действующие серии, то есть
   * договорённости, и отвязанная пара свою не теряет (перевыпуск ссылки
   * возвращает занятия сразу, раздел 3 `docs/group-lessons-design.md`). Ровно
   * так же считает состав сервер на экране, куда ведёт строка
   * (`members` в `GroupScheduleView` собирается из `activeSeries`), и это важнее
   * совпадения с сеткой: разойтись с тем, что откроется по нажатию, значит
   * соврать через одно движение.
   */
  size: number | null;
  /** Серия, у которой берутся общие поля: дни, длительность, зона. */
  base: ScheduleSeriesView;
  startsOn: string;
  endsOn: string | null;
}

/**
 * Ответ сервера на две секции: серии пар и пучки групп.
 *
 * ⚠️ **Групповую серию узнаём по полю `groupId`, а не по роли смотрящего.**
 * Поля этого нет в ответе ученику НИ У ОДНОЙ серии (`ScheduleSeriesView`,
 * раздел 3 `docs/group-lessons-design.md`): занятие группы в его расписании это
 * его занятие без признаков группы. Значит у ученика все серии попадают в
 * первую секцию, как попадали до этой правки, а вторая не появляется вовсе.
 * Роль при этом у каждой серии своя (у одного и того же человека бывают и
 * ученики, и свои преподаватели), поэтому отбор по ней был бы вдобавок
 * неверным: он делит список не там, где надо.
 */
export function foldSeries(
  series: readonly ScheduleSeriesView[],
  now: number,
): { pairs: ScheduleSeriesView[]; groups: BunchView[] } {
  const pairs: ScheduleSeriesView[] = [];
  // Порядок секции задаёт порядок ответа (у серий он по возрасту), а `Map`
  // держит порядок первого попадания: группы встают так же, как встали бы их
  // серии
  const kin = new Map<string, ScheduleSeriesView[]>();
  for (const s of series) {
    const groupId = s.groupId ?? null;
    if (groupId === null) {
      pairs.push(s);
      continue;
    }
    const same = kin.get(groupId);
    if (same === undefined) kin.set(groupId, [s]);
    else same.push(s);
  }

  const groups: BunchView[] = [];
  for (const [groupId, members] of kin) {
    const view = bunchView(groupId, members, now);
    if (view !== null) groups.push(view);
  }
  return { pairs, groups };
}

/**
 * Строка пучка по его сериям.
 *
 * ⚠️ **Границы и число участников считаются по ДЕЙСТВУЮЩИМ сериям**, и отбирает
 * их общее правило `seriesActiveAt` из `@edu/shared`, а сами границы общая
 * `bunchBounds` оттуда же. Своей арифметики по датам здесь нет намеренно: то же
 * правило и на том же входном наборе зовёт сервер (`groupScheduleView` в
 * `repo/schedule-groups.ts`), а вторая копия разошлась бы с первой молча. Видно
 * расхождение было бы в начале пучка: у ушедшего участника дата начала при
 * закрытии серии не двигается, и по всем сериям подряд экран сказал бы
 * «с 1 сентября» там, где расписание группы говорит «со 2 ноября».
 *
 * ⚠️ **Пучок, у которого не осталось ни одной действующей серии, из списка не
 * исчезает.** Расписание группы кончилось, но договорённость была, и убрать её
 * из списка значит потерять единственное место, где она видна: в секцию пар
 * такие серии не попадают, у них есть `groupId`. Границы тогда считаются по
 * всему пучку целиком, а число участников не считается вовсе (`size` это
 * `null`): ноль читался бы как «в группе никого не осталось», а осталось там
 * ровно столько людей, сколько было, кончилось у них расписание.
 */
function bunchView(
  groupId: string,
  members: readonly ScheduleSeriesView[],
  now: number,
): BunchView | null {
  const active = members.filter((s) => seriesActiveAt(s, now));
  const shown = active.length > 0 ? active : members;
  /*
    Общие поля берутся у той же серии, что и на сервере: наименьшей по
    идентификатору (`baseSeries` в `repo/schedule-groups.ts`). Сёстрам их правит
    только массовая ручка, поэтому подошла бы любая, а фиксированный выбор
    означает, что расхождение, если оно однажды случится, будет видно при каждом
    открытии экрана, а не через раз.
  */
  const base = [...shown].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))[0];
  const bounds = bunchBounds(shown);
  // Пустым пучок не бывает: он заводится первой же попавшей в него серией.
  // Строка нужна ради сужения типа, а не ради нового поведения, тем же приёмом
  // сужает себя сервер
  if (base === undefined || bounds === null) return null;
  return {
    groupId,
    title: base.groupTitle ?? null,
    size: active.length > 0 ? active.length : null,
    base,
    startsOn: bounds.startsOn,
    endsOn: bounds.endsOn,
  };
}
